// fetchVaadsMessages.jsx - Updated with notification support for answers

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7204';

// Get all vaads messages from all buildings
export const getVaadsMessages = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/VaadsMessage/GetVaadsMessages`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });

    if (!response.ok) {
      const errorData = await response.text();
      throw new Error(errorData);
    }

    return await response.json();
  } catch (error) {
    throw error;
  }
};

// Get vaads messages by building (kept for backward compatibility)
export const getVaadsMessagesByBuilding = async (buildingId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/VaadsMessage/GetVaadsMessagesByBuilding/${buildingId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });

    if (!response.ok) {
      const errorData = await response.text();
      throw new Error(errorData);
    }

    return await response.json();
  } catch (error) {
    throw error;
  }
};

// Create new vaads message
export const createVaadsMessage = async (messageData) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/VaadsMessage`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify(messageData)
    });

    if (!response.ok) {
      const errorData = await response.text();
      throw new Error(errorData);
    }

    const newMessage = await response.json();
    
    // Dispatch event to notify other components about new message
    window.dispatchEvent(new CustomEvent('vaadsMessageCreated', {
      detail: { message: newMessage }
    }));

    return newMessage;
  } catch (error) {
    throw error;
  }
};

// Update vaads message
export const updateVaadsMessage = async (messageId, messageData) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/VaadsMessage/${messageId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify(messageData)
    });

    if (!response.ok) {
      const errorData = await response.text();
      throw new Error(errorData);
    }

    return await response.json();
  } catch (error) {
    throw error;
  }
};

// Delete vaads message
export const deleteVaadsMessage = async (messageId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/VaadsMessage/${messageId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });

    if (!response.ok) {
      const errorData = await response.text();
      throw new Error(errorData);
    }

    return await response.json();
  } catch (error) {
    throw error;
  }
};

// Check if user has unread messages (simple boolean)
export const hasUnreadMessages = async (userId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/VaadsMessage/has-unread/${userId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });

    if (!response.ok) {
      // Fallback to old API if new one doesn't exist
      if (response.status === 404) {
        const countResult = await getUnreadMessagesCount(userId);
        return { hasUnread: (countResult.unreadCount || 0) > 0 };
      }
      
      const errorData = await response.text();
      throw new Error(errorData);
    }

    return await response.json();
  } catch (error) {
    
    // Graceful fallback - if API fails, return false
    return { hasUnread: false };
  }
};

// Get unread messages count - UPDATED to work with both old and new API
export const getUnreadMessagesCount = async (userId, buildingId = null) => {
  try {
    let url;
    
    // Try the new cross-building API first (without buildingId)
    if (buildingId === null) {
      url = `${API_BASE_URL}/api/VaadsMessage/unread-count/${userId}`;
    } else {
      // Fallback to old building-specific API if buildingId is provided
      url = `${API_BASE_URL}/api/VaadsMessage/GetUnreadMessagesCount/${userId}/${buildingId}`;
    }

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });

    if (!response.ok) {
      // If the new API doesn't exist yet, try the old API format
      if (response.status === 404 && buildingId === null) {
        
        // Get current user's building ID from localStorage
        const userData = JSON.parse(localStorage.getItem('user') || '{}');
        const userBuildingId = userData.buildingId;
        
        if (userBuildingId) {
          return await getUnreadMessagesCount(userId, userBuildingId);
        } else {
          // If no building ID available, return 0 unread messages
          return { unreadCount: 0, hasUnread: false };
        }
      }
      
      const errorData = await response.text();
      throw new Error(errorData);
    }

    return await response.json();
  } catch (error) {
    
    // Graceful fallback - if API fails, return no unread messages
    return { unreadCount: 0, hasUnread: false };
  }
};

// Mark messages as read - UPDATED to work with both old and new API
export const markMessagesAsRead = async (userId, buildingId = null) => {
  try {
    let url;
    let body;
    
    // Try the new cross-building API first (without buildingId)
    if (buildingId === null) {
      url = `${API_BASE_URL}/api/VaadsMessage/mark-read/${userId}`;
      body = JSON.stringify({});
    } else {
      // Fallback to old building-specific API if buildingId is provided
      url = `${API_BASE_URL}/api/VaadsMessage/MarkMessagesAsRead/${userId}/${buildingId}`;
      body = JSON.stringify({ buildingId });
    }

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: body
    });

    if (!response.ok) {
      // If the new API doesn't exist yet, try the old API format
      if (response.status === 404 && buildingId === null) {
        
        // Get current user's building ID from localStorage
        const userData = JSON.parse(localStorage.getItem('user') || '{}');
        const userBuildingId = userData.buildingId;
        
        if (userBuildingId) {
          return await markMessagesAsRead(userId, userBuildingId);
        } else {
          // If no building ID available, return success
          return { success: true };
        }
      }
      
      const errorData = await response.text();
      throw new Error(errorData);
    }

    const result = await response.json();
    
    // Dispatch event to notify other components about messages being read
    window.dispatchEvent(new CustomEvent('vaadsMessagesRead', {
      detail: { userId }
    }));

    return result;
  } catch (error) {
    
    // Graceful fallback - if API fails, return success
    // This prevents the app from crashing while backend is being updated
    return { success: true };
  }
};

// NEW: Reset read status for a specific message (for when answers are added)
export const resetMessageReadStatus = async (messageId, currentUserId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/VaadsMessage/${messageId}/reset-read-status`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify({ excludeUserId: currentUserId })
    });

    if (!response.ok) {
      // If this new API doesn't exist yet, we'll fall back to older behavior
      if (response.status === 404) {
        return { success: true };
      }
      
      const errorData = await response.text();
      throw new Error(errorData);
    }

    return await response.json();
  } catch (error) {
    // Don't throw error - this is a nice-to-have feature
    return { success: false };
  }
};

// Get specific vaads message
export const getVaadsMessage = async (id) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/VaadsMessage/${id}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });

    if (!response.ok) {
      const errorData = await response.text();
      throw new Error(errorData);
    }

    return await response.json();
  } catch (error) {
    throw error;
  }
};

// Add answer to message - UPDATED to handle notifications
export const addAnswerToMessage = async (messageId, answerData) => {
  try {
    // Ensure the data has the correct property names that match the C# class
    const requestData = {
      AnswerText: answerData.answerText || answerData.AnswerText,
      UserID: answerData.userID || answerData.UserID  // FIXED: Use UserID (uppercase)
    };

    const response = await fetch(`${API_BASE_URL}/api/VaadsMessage/${messageId}/answer`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify(requestData)  // FIXED: Use requestData with correct property names
    });

    if (!response.ok) {
      const errorData = await response.text();
      throw new Error(errorData);
    }

    const result = await response.json();
    
    // After successfully adding the answer, reset read status for other users
    try {
      await resetMessageReadStatus(messageId, requestData.UserID);
    } catch (error) {
    }

    // Dispatch event to notify other components about new answer
    window.dispatchEvent(new CustomEvent('vaadsMessageAnswered', {
      detail: { 
        messageId: messageId,
        answer: result.answer,
        answerUserId: requestData.UserID
      }
    }));

    return result;
  } catch (error) {
    throw error;
  }
};

export default {
  getVaadsMessages,
  getVaadsMessagesByBuilding,
  createVaadsMessage,
  updateVaadsMessage,
  deleteVaadsMessage,
  getUnreadMessagesCount,
  hasUnreadMessages,
  markMessagesAsRead,
  resetMessageReadStatus,
  getVaadsMessage,
  addAnswerToMessage
};